(() => {
  'use strict';

  const PATCH_VERSION = 'fix5-cart-reliability-2026-08-06';
  const RUNTIME_KEY = '__gdcCartReliabilityRuntime';
  const existingRuntime = window[RUNTIME_KEY];

  if (existingRuntime?.initialized) {
    existingRuntime.assetLoads += 1;
    return;
  }

  const runtime = {
    version: PATCH_VERSION,
    initialized: true,
    assetLoads: 1,
    loggedErrors: existingRuntime?.loggedErrors || new Set(),
    checkoutListenerBound: false,
    cartQuantityIntentListenerBound: false,
    cartQuantityIntents: new WeakMap(),
    cartUpdateTimeline: [],
    patchPromisesScheduled: false,
    diagnostics: {
      cartPrototypePatches: 0,
      cartItemsPrototypePatches: 0,
      cartUpdateRequests: 0,
      cartUpdateFailures: 0,
      cartUpdateSectionFailures: 0,
      cartQuantityIntents: 0,
      checkoutListeners: 0,
      pageshowListeners: 0,
      mutationObservers: 0,
      recoveryStarts: 0,
      recoveryFallbacks: 0,
    },
  };
  window[RUNTIME_KEY] = runtime;

  const reportError = (code, error) => {
    if (runtime.loggedErrors.has(code)) return;
    runtime.loggedErrors.add(code);
    console.error(`[GutsDog cart] ${code}`, {
      patch: PATCH_VERSION,
      message: error instanceof Error ? error.message : String(error || code),
    });
  };

  const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  const recordCartUpdate = (entry) => {
    runtime.cartUpdateTimeline.push({
      at: Date.now(),
      ...entry,
    });
    if (runtime.cartUpdateTimeline.length > 20) runtime.cartUpdateTimeline.shift();
  };

  const parseSection = (html, selector = '.shopify-section') => {
    if (typeof html !== 'string' || html.trim() === '') {
      throw new Error('Section HTML is missing or empty');
    }

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const section = documentFragment.querySelector(selector);
    if (!section) throw new Error(`Section selector not found: ${selector}`);
    return section;
  };

  const getRecoveryNotice = (drawer) => {
    let notice = drawer.querySelector('[data-gdc-cart-recovery]');
    if (notice) return notice;

    notice = document.createElement('div');
    notice.className = 'gdc-cart-recovery';
    notice.hidden = true;
    notice.dataset.gdcCartRecovery = '';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.innerHTML = `
      <p class="gdc-cart-recovery__message">Item added to cart.</p>
      <div class="gdc-cart-recovery__actions">
        <a class="button button--secondary" href="/cart">View cart</a>
        <a class="button gdc-cart-recovery__checkout" href="/checkout">Checkout</a>
      </div>
    `;
    drawer.appendChild(notice);
    return notice;
  };

  const hideRecoveryNotice = (drawer) => {
    const notice = drawer.querySelector('[data-gdc-cart-recovery]');
    if (notice) notice.hidden = true;
  };

  const showRecoveryNotice = (drawer, reason) => {
    const notice = getRecoveryNotice(drawer);
    runtime.diagnostics.recoveryFallbacks += 1;
    notice.hidden = false;
    drawer.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    notice.querySelector('a')?.focus();

    drawer.dispatchEvent(
      new CustomEvent('gdc:cart-render-failed', {
        bubbles: true,
        detail: { reason, cartAddSucceeded: true },
      })
    );
  };

  const renderCartSections = (drawer, state) => {
    if (!state || typeof state !== 'object') throw new Error('Cart response is missing');
    if (!state.sections || typeof state.sections !== 'object') {
      throw new Error('Cart section response is missing');
    }

    const drawerHtml = state.sections['cart-drawer'];
    const drawerDestination = document.querySelector('#CartDrawer');
    if (!drawerDestination) throw new Error('#CartDrawer is not mounted');

    const drawerSource = parseSection(drawerHtml, '#CartDrawer');
    const iconHtml = state.sections['cart-icon-bubble'];
    const iconDestination = document.getElementById('cart-icon-bubble');
    const iconSource =
      typeof iconHtml === 'string' && iconHtml.trim() !== ''
        ? parseSection(iconHtml, '.shopify-section')
        : null;

    drawerDestination.innerHTML = drawerSource.innerHTML;
    if (iconDestination && iconSource) iconDestination.innerHTML = iconSource.innerHTML;

    drawer.productId = state.id;
    drawer.classList.remove('is-empty');
    drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');

    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (!overlay) throw new Error('#CartDrawer-Overlay is missing after render');
    overlay.addEventListener('click', drawer.close.bind(drawer), { once: true });

    hideRecoveryNotice(drawer);
    window.setTimeout(() => drawer.open(), 0);
    drawer.dispatchEvent(new CustomEvent('gdc:cart-state-changed', { bubbles: true }));
  };

  const getRetryDelay = (response) => {
    const retryAfter = response?.headers?.get?.('Retry-After');
    if (retryAfter) {
      const seconds = Number(retryAfter);
      if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
      const retryDate = Date.parse(retryAfter);
      if (Number.isFinite(retryDate)) return Math.max(0, retryDate - Date.now());
    }
    return 750 + Math.floor(Math.random() * 251);
  };

  const fetchCartSections = async (externalSignal) => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';
    const url = `${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble`;
    let lastError;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 4000);
      const abortFromExternal = () => controller.abort();
      externalSignal?.addEventListener('abort', abortFromExternal, { once: true });

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });

        if (!response.ok) {
          const error = new Error(`Cart section refresh returned HTTP ${response.status}`);
          error.retryable = response.status === 429 || response.status >= 500;
          error.retryAfterMs = getRetryDelay(response);
          throw error;
        }

        const sections = await response.json();
        if (!sections || typeof sections !== 'object') {
          throw new Error('Cart section refresh returned invalid JSON');
        }
        return sections;
      } catch (error) {
        lastError = error;
        const retryable =
          error?.retryable || error?.name === 'AbortError' || error instanceof TypeError;
        if (externalSignal?.aborted || !retryable || attempt === 1) break;
        await delay(error?.retryAfterMs ?? 750 + Math.floor(Math.random() * 251));
      } finally {
        window.clearTimeout(timeout);
        externalSignal?.removeEventListener('abort', abortFromExternal);
      }
    }

    throw lastError || new Error('Cart section refresh failed');
  };

  const recoverCartDrawer = async (drawer, state, initialError, generation) => {
    const existing = drawer.__gdcRecoveryContext;
    if (existing?.generation === generation) return existing.promise;
    if (existing?.stateId && existing.stateId === String(state?.id || '')) {
      drawer.__gdcRenderGeneration = existing.generation;
      return existing.promise;
    }
    existing?.abortController.abort();

    const context = {
      generation,
      stateId: String(state?.id || ''),
      abortController: new AbortController(),
      promise: null,
    };
    runtime.diagnostics.recoveryStarts += 1;
    context.promise = (async () => {
      try {
        const sections = await fetchCartSections(context.abortController.signal);
        if (generation !== drawer.__gdcRenderGeneration) return;
        renderCartSections(drawer, { id: state?.id, sections });
      } catch (refreshError) {
        if (
          context.abortController.signal.aborted ||
          generation !== drawer.__gdcRenderGeneration
        ) {
          return;
        }
        reportError('drawer-render-recovery-failed', refreshError);
        showRecoveryNotice(drawer, initialError?.message || refreshError?.message || 'unknown');
      } finally {
        if (drawer.__gdcRecoveryContext === context) {
          drawer.__gdcRecoveryContext = null;
        }
      }
    })();
    drawer.__gdcRecoveryContext = context;
    return context.promise;
  };

  const patchCartDrawer = () => {
    const CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype.__gdcReliabilityPatched) return;

    const prototype = CartDrawer.prototype;
    prototype.__gdcReliabilityPatched = {
      version: PATCH_VERSION,
      strategy: 'prototype-method-replacement',
    };
    runtime.diagnostics.cartPrototypePatches += 1;

    prototype.getSectionInnerHTML = (html, selector = '.shopify-section') =>
      parseSection(html, selector).innerHTML;
    prototype.getSectionDOM = (html, selector = '.shopify-section') =>
      parseSection(html, selector);

    prototype.renderContents = function renderContents(state) {
      this.__gdcRenderGeneration = (this.__gdcRenderGeneration || 0) + 1;
      const generation = this.__gdcRenderGeneration;
      this.__gdcRecoveryContext?.abortController.abort();
      try {
        renderCartSections(this, state);
      } catch (error) {
        reportError('drawer-render-failed', error);
        void recoverCartDrawer(this, state, error, generation);
      }
    };
  };

  const getCartUpdateErrorHost = (cartItems) => {
    const existing =
      document.getElementById('cart-errors') ||
      document.getElementById('CartDrawer-CartErrors');
    if (existing) return existing;

    const host = document.createElement('div');
    host.dataset.gdcCartUpdateError = '';
    host.setAttribute('role', 'alert');
    cartItems.append(host);
    return host;
  };

  const clearCartUpdateError = () => {
    document
      .querySelectorAll('#cart-errors, #CartDrawer-CartErrors, [data-gdc-cart-update-error]')
      .forEach((host) => host.replaceChildren());
    document.querySelectorAll('[data-gdc-cart-update-checkout-disabled]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      delete button.dataset.gdcCartUpdateCheckoutDisabled;
    });
  };

  const showCartUpdateError = (
    cartItems,
    message,
    totalMayBeStale = false,
    preserveConfirmedTotal = false
  ) => {
    const host = getCartUpdateErrorHost(cartItems);
    const reloadLink = document.createElement('a');
    reloadLink.href = window.routes?.cart_url || '/cart';
    reloadLink.className = 'link';
    reloadLink.textContent = 'Reload cart';
    host.replaceChildren(document.createTextNode(`${message} `), reloadLink);

    if (!totalMayBeStale) return;
    if (!preserveConfirmedTotal) {
      document
        .querySelectorAll('#CartDrawer .totals__total-value, #main-cart-footer .totals__total-value')
        .forEach((total) => {
          total.textContent = 'Reload cart to confirm total.';
          total.dataset.gdcCartTotalStale = '';
        });
    }
    document.querySelectorAll('#CartDrawer-Checkout, #checkout').forEach((button) => {
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.gdcCartUpdateCheckoutDisabled = '';
    });
  };

  const getCartLineKey = (cartItems, line, event) => {
    const target = event?.target;
    const currentTarget = event?.currentTarget;
    const row =
      target?.closest?.('.cart-item') ||
      currentTarget?.closest?.('.cart-item') ||
      cartItems.querySelector(`#CartItem-${CSS.escape(String(line))}`) ||
      cartItems.querySelector(`#CartDrawer-Item-${CSS.escape(String(line))}`);
    const explicitKey =
      target?.dataset?.lineKey ||
      currentTarget?.dataset?.lineKey ||
      row?.dataset?.lineKey;
    if (explicitKey) return explicitKey;

    const removeLink = row?.querySelector('cart-remove-button a[href]');
    if (!removeLink?.href) return '';
    try {
      return new URL(removeLink.href, window.location.origin).searchParams.get('id') || '';
    } catch {
      return '';
    }
  };

  const setCartUpdateControlsLocked = (cartItems, locked) => {
    if (locked) {
      cartItems.querySelectorAll('quantity-input button, .quantity__input, cart-remove-button button, cart-remove-button a').forEach(
        (control) => {
          if (control.disabled || control.getAttribute('aria-disabled') === 'true') return;
          if ('disabled' in control) control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
          control.dataset.gdcCartUpdateLocked = '';
        }
      );
      return;
    }

    document.querySelectorAll('[data-gdc-cart-update-locked]').forEach((control) => {
      if ('disabled' in control) control.disabled = false;
      control.removeAttribute('aria-disabled');
      delete control.dataset.gdcCartUpdateLocked;
    });
  };

  const bindCartInteractionLock = () => {
    if (runtime.cartInteractionLockListenerBound) return;
    runtime.cartInteractionLockListenerBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const control = event.target?.closest?.('quantity-input button, cart-remove-button a');
        if (!control || control.getAttribute('aria-disabled') !== 'true') return;
        if (!control.closest('cart-items, cart-drawer-items')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  };

  const clearCartQuantityIntent = (input, outcome) => {
    const intent = input && runtime.cartQuantityIntents.get(input);
    if (!intent) return null;
    window.clearTimeout(intent.watchdog);
    runtime.cartQuantityIntents.delete(input);
    if (outcome) {
      recordCartUpdate({
        type: 'quantity-intent',
        intentId: intent.id,
        outcome,
        durationMs: Math.round(performance.now() - intent.startedAt),
      });
    }
    return intent;
  };

  const bindCartQuantityIntentListener = () => {
    if (runtime.cartQuantityIntentListenerBound) return;
    runtime.cartQuantityIntentListenerBound = true;

    document.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!input?.matches?.('.quantity__input')) return;
        const cartItems = input.closest('cart-items, cart-drawer-items');
        if (!cartItems || typeof cartItems.enableLoading !== 'function') return;

        const requestedQuantity = Number.parseInt(input.value, 10);
        const confirmedQuantity = Number.parseInt(
          input.getAttribute('value') ?? input.defaultValue,
          10
        );
        if (!Number.isFinite(requestedQuantity) || requestedQuantity === confirmedQuantity) return;

        const existing = runtime.cartQuantityIntents.get(input);
        if (existing) window.clearTimeout(existing.watchdog);
        const intent = {
          id: (runtime.cartQuantityIntentGeneration || 0) + 1,
          requestedQuantity,
          confirmedQuantity,
          startedAt: performance.now(),
          claimed: false,
          watchdog: null,
        };
        runtime.cartQuantityIntentGeneration = intent.id;
        runtime.cartQuantityIntents.set(input, intent);
        runtime.diagnostics.cartQuantityIntents += 1;
        input.value = String(confirmedQuantity);
        queueMicrotask(() => {
          if (runtime.cartQuantityIntents.get(input) === intent && !intent.claimed) {
            input.value = String(confirmedQuantity);
          }
        });
        cartItems.enableLoading(input.dataset.index);
        clearCartUpdateError();
        recordCartUpdate({
          type: 'quantity-intent',
          intentId: intent.id,
          line: input.dataset.index,
          requestedQuantity,
          outcome: 'feedback-visible',
        });

        intent.watchdog = window.setTimeout(() => {
          if (runtime.cartQuantityIntents.get(input) !== intent || intent.claimed) return;
          input.value = String(confirmedQuantity);
          clearCartQuantityIntent(input, 'debounced-handler-missing');
          cartItems.disableLoading();
          showCartUpdateError(cartItems, "Couldn't update cart. Try again.");
        }, 1500);
      },
      true
    );
  };

  const renderCartUpdateSections = (cartItems, state, sections) => {
    const rendered = [];
    const failed = [];

    sections.forEach((section) => {
      let handled = false;
      try {
        const destinationRoot = document.getElementById(section.id);
        if (!destinationRoot) throw new Error(`Destination section is missing: ${section.id}`);
        const destination = section.selector
          ? destinationRoot.querySelector(section.selector)
          : destinationRoot;
        if (!destination) {
          throw new Error(`Destination selector is missing: ${section.selector}`);
        }
        const sourceHtml = state.sections?.[section.section];
        const source = parseSection(sourceHtml, section.selector || '.shopify-section');
        destination.innerHTML = source.innerHTML;
        rendered.push(section.section);
        handled = true;
      } catch (error) {
        if (section.section === 'cart-icon-bubble') {
          const icon = document.getElementById('cart-icon-bubble');
          const sourceHtml = state.sections?.[section.section];
          const iconSource =
            typeof sourceHtml === 'string' && sourceHtml.trim() !== ''
              ? new DOMParser().parseFromString(sourceHtml, 'text/html').querySelector('#cart-icon-bubble')
              : null;
          const countBubble = icon?.querySelector('.cart-count-bubble');
          const plainSection = icon?.querySelector('.shopify-section');
          const directCount = icon?.querySelector(':scope > span') || icon?.querySelector('span');

          if (icon && iconSource) {
            icon.innerHTML = iconSource.innerHTML;
            rendered.push(section.section);
            handled = true;
          } else if (countBubble) {
            const countNodes = countBubble.querySelectorAll('span');
            if (countNodes[0]) countNodes[0].textContent = String(state.item_count);
            if (countNodes[1]) {
              countNodes[1].textContent = `${state.item_count} item${state.item_count === 1 ? '' : 's'}`;
            }
            countBubble.hidden = state.item_count === 0;
            rendered.push(section.section);
            handled = true;
          } else if (plainSection) {
            plainSection.textContent = String(state.item_count);
            rendered.push(section.section);
            handled = true;
          } else if (directCount) {
            directCount.textContent = String(state.item_count);
            rendered.push(section.section);
            handled = true;
          }
        }

        if (!handled && section.section === 'cart-live-region-text') {
          // Accessibility-only live-region output may be absent from a section response.
          // Native updateLiveRegions still updates the existing live region in the DOM.
          handled = true;
          rendered.push(section.section);
        }

        if (!handled) {
          runtime.diagnostics.cartUpdateSectionFailures += 1;
          failed.push({ section: section.section, message: error.message });
          reportError(`cart-update-section-${section.section}`, error);
        }
      }
    });

    const requiredDestinationIds =
      cartItems.tagName === 'CART-DRAWER-ITEMS'
        ? ['CartDrawer']
        : ['main-cart-items', 'main-cart-footer'];
    return {
      rendered,
      failed,
      criticalFailure: requiredDestinationIds.some((id) => {
        const descriptor = sections.find((section) => section.id === id);
        return !descriptor || !rendered.includes(descriptor.section);
      }),
    };
  };

  const patchCartItems = () => {
    const CartItems = customElements.get('cart-items');
    if (!CartItems || CartItems.prototype.__gdcQuantityReliabilityPatched) return;

    const prototype = CartItems.prototype;
    prototype.__gdcQuantityReliabilityPatched = {
      version: PATCH_VERSION,
      strategy: 'prototype-method-replacement',
    };
    runtime.diagnostics.cartItemsPrototypePatches += 1;

    prototype.resetQuantityInput = function resetQuantityInput(id, inputHint) {
      const input =
        inputHint ||
        this.querySelector(`#Quantity-${CSS.escape(String(id))}`) ||
        this.querySelector(`#Drawer-quantity-${CSS.escape(String(id))}`);
      if (!input) return false;
      input.value = input.getAttribute('value') ?? input.defaultValue ?? input.value;
      this.isEnterPressed = false;
      return true;
    };

    prototype.validateQuantity = function validateQuantity(event) {
      const input = event?.target;
      if (!input) return;
      const intent = runtime.cartQuantityIntents.get(input);
      if (intent) {
        intent.claimed = true;
        window.clearTimeout(intent.watchdog);
      }
      const inputValue = intent?.requestedQuantity ?? Number.parseInt(input.value, 10);
      const index = input.dataset.index;
      const minimum = Number.parseInt(input.dataset.min, 10);
      const maximum = Number.parseInt(input.max, 10);
      const step = Number.parseInt(input.step, 10) || 1;
      let message = '';

      if (!Number.isFinite(inputValue) || inputValue < 0) {
        message = window.cartStrings?.error || 'Cart update failed.';
      } else if (inputValue !== 0 && Number.isFinite(minimum) && inputValue < minimum) {
        message = window.quickOrderListStrings.min_error.replace('[min]', minimum);
      } else if (Number.isFinite(maximum) && inputValue > maximum) {
        message = window.quickOrderListStrings.max_error.replace('[max]', maximum);
      } else if (inputValue !== 0 && inputValue % step !== 0) {
        message = window.quickOrderListStrings.step_error.replace('[step]', step);
      }

      if (message) {
        input.setCustomValidity(message);
        input.reportValidity();
        this.resetQuantityInput(index, input);
        clearCartQuantityIntent(input, 'validation-failed');
        this.disableLoading();
        input.select?.();
        return;
      }

      input.setCustomValidity('');
      input.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement?.getAttribute?.('name') || input.name,
        input.dataset.quantityVariantId,
        intent
      );
    };

    prototype.updateLiveRegions = function updateLiveRegions(line, message) {
      const lineItemError =
        document.getElementById(`Line-item-error-${line}`) ||
        document.getElementById(`CartDrawer-LineItemError-${line}`);
      const errorText = lineItemError?.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = message || '';

      const lineStatus =
        (this.lineItemStatusElement?.isConnected && this.lineItemStatusElement) ||
        document.getElementById('shopping-cart-line-item-status') ||
        document.getElementById('CartDrawer-LineItemStatus');
      lineStatus?.setAttribute('aria-hidden', 'true');

      const cartStatus =
        document.getElementById('cart-live-region-text') ||
        document.getElementById('CartDrawer-LiveRegionText');
      if (!cartStatus) return;
      cartStatus.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => cartStatus.setAttribute('aria-hidden', 'true'), 1000);
    };

    prototype.enableLoading = function enableLoading(line) {
      document
        .querySelectorAll('#main-cart-items, #CartDrawer-CartItems')
        .forEach((root) => root.classList.add('cart__items--disabled'));
      this.querySelectorAll(
        `#CartItem-${CSS.escape(String(line))} .loading__spinner, #CartDrawer-Item-${CSS.escape(
          String(line)
        )} .loading__spinner`
      ).forEach((spinner) => spinner.classList.remove('hidden'));
      setCartUpdateControlsLocked(this, true);
      document.activeElement?.blur?.();

      const lineStatus =
        (this.lineItemStatusElement?.isConnected && this.lineItemStatusElement) ||
        document.getElementById('shopping-cart-line-item-status') ||
        document.getElementById('CartDrawer-LineItemStatus');
      if (lineStatus) {
        this.lineItemStatusElement = lineStatus;
        lineStatus.setAttribute('aria-hidden', 'false');
      }
    };

    prototype.disableLoading = function disableLoading() {
      document
        .querySelectorAll('#main-cart-items, #CartDrawer-CartItems')
        .forEach((root) => root.classList.remove('cart__items--disabled'));
      document
        .querySelectorAll('cart-items .loading__spinner, cart-drawer-items .loading__spinner')
        .forEach((spinner) => spinner.classList.add('hidden'));
      setCartUpdateControlsLocked(this, false);
      document
        .querySelectorAll('#shopping-cart-line-item-status, #CartDrawer-LineItemStatus')
        .forEach((status) => status.setAttribute('aria-hidden', 'true'));
    };

    prototype.updateQuantity = async function updateQuantity(line, quantity, event, name, variantId) {
      if (runtime.cartUpdateContext?.pending) {
        this.resetQuantityInput(line, event?.target?.matches?.('.quantity__input') ? event.target : null);
        return;
      }

      const lineNumber = Number.parseInt(line, 10);
      const nextQuantity = Number.parseInt(quantity, 10);
      if (!Number.isFinite(lineNumber) || lineNumber < 1 || !Number.isFinite(nextQuantity) || nextQuantity < 0) {
        this.resetQuantityInput(line, event?.target?.matches?.('.quantity__input') ? event.target : null);
        return;
      }

      const generation = (runtime.cartUpdateGeneration || 0) + 1;
      runtime.cartUpdateGeneration = generation;
      const controller = new AbortController();
      const context = {
        generation,
        requestId: generation,
        pending: true,
        controller,
        startedAt: performance.now(),
        timedOut: false,
      };
      runtime.cartUpdateContext = context;
      const timeout = window.setTimeout(() => {
        context.timedOut = true;
        controller.abort();
      }, 12000);

      this.enableLoading(line);
      clearCartUpdateError();

      try {
        const sections = this.getSectionsToRender().filter((section) => section?.section);
        if (!sections.length) throw new Error('No cart sections are configured for update');

        const body = {
          line: lineNumber,
          quantity: nextQuantity,
          sections: [...new Set(sections.map((section) => section.section))],
          sections_url: window.location.pathname,
        };
        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          ...fetchConfig(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        const responseText = await response.text();
        let state;
        try {
          state = JSON.parse(responseText);
        } catch {
          throw new Error('Cart change returned invalid JSON');
        }
        if (!response.ok || state?.errors) {
          const error = new Error(typeof state?.errors === 'string' ? state.errors : 'Cart change was rejected');
          error.cartMayHaveChanged = response.status >= 500;
          error.expectedCartError = Boolean(state?.errors && response.ok);
          throw error;
        }
        if (generation !== runtime.cartUpdateGeneration) {
          this.resetQuantityInput(line, event?.target?.matches?.('.quantity__input') ? event.target : null);
          return;
        }

        this.classList.toggle('is-empty', state.item_count === 0);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', state.item_count === 0);
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', state.item_count === 0);

        const renderResult = renderCartUpdateSections(this, state, sections);
        if (renderResult.criticalFailure) throw new Error('Required cart section did not render');

        clearCartUpdateError();
        this.updateLiveRegions(line, '');
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, variantId });
        } catch (error) {
          reportError('cart-update-publish-failed', error);
        }
      } catch (error) {
        if (generation !== runtime.cartUpdateGeneration) return;
        if (!error?.expectedCartError) reportError('cart-quantity-update-failed', error);
        const timedOut = error?.name === 'AbortError' && context.timedOut;
        showCartUpdateError(
          this,
          error?.userMessage || error?.message || "Couldn't update cart. Try again.",
          Boolean(error?.cartMayHaveChanged ?? timedOut),
          true
        );
        this.resetQuantityInput(line, event?.target?.matches?.('.quantity__input') ? event.target : null);
      } finally {
        window.clearTimeout(timeout);
        context.pending = false;
        if (runtime.cartUpdateContext === context) runtime.cartUpdateContext = null;
        clearCartQuantityIntent(event?.target);
        this.disableLoading();
      }
    };
  };

  const patchCheckoutState = () => {
    if (runtime.checkoutListenerBound) return;
    runtime.checkoutListenerBound = true;
    runtime.diagnostics.checkoutListeners += 1;

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('#CartDrawer-Checkout');
        if (!button || button.disabled) return;
        if (button.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        window.setTimeout(() => {
          button.disabled = true;
        }, 0);
      },
      true
    );

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      document.querySelectorAll('#CartDrawer-Checkout.is-loading').forEach((button) => {
        button.classList.remove('is-loading');
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
      });
    });
    runtime.diagnostics.pageshowListeners += 1;
  };

  runtime.patchPromisesScheduled = true;
  customElements.whenDefined('cart-drawer').then(patchCartDrawer).catch((error) => {
    reportError('cart-drawer-definition-failed', error);
  });
  customElements.whenDefined('cart-items').then(() => {
    patchCartItems();
  }).catch((error) => {
    reportError('cart-items-definition-failed', error);
  });
  patchCheckoutState();
  bindCartInteractionLock();
  bindCartQuantityIntentListener();
})();
